/**
 * SetOps Store
 * Manages playlist set operation state and workflow
 */

import { create } from 'zustand';
import type { Track, PlaylistSummary } from '../api/types';
import { getApiClient } from '../api/client';

export type SetOperationType = 'union' | 'intersection' | 'difference' | 'symmetric_difference';
export type MatchMode = 'strict' | 'isrc' | 'fuzzy' | 'smart';

/**
 * Metadata about the set operation result
 */
interface SetOpsResultMetadata {
  operation: SetOperationType;
  sourceAName: string;
  sourceBName: string;
  matchMode: MatchMode;
  duplicatesFound: number;
  isrcMatches: number;
  fuzzyMatches: number;
  executionTimeMs: number;
}

/**
 * API response structure for set operation execution
 */
interface SetOpExecuteResponse {
  result_tracks: Track[];
  operation: SetOperationType;
  source_a_name: string;
  source_b_name: string;
  match_mode: MatchMode;
  duplicates_found: number;
  isrc_matches: number;
  fuzzy_matches: number;
  execution_time_ms: number;
}

/**
 * API response structure for preview
 */
interface SetOpPreviewResponse {
  estimated_count: number;
  operation: SetOperationType;
}

/**
 * SetOps store state interface
 */
export interface SetOpsState {
  // Drag state
  isDragging: boolean;
  draggedPlaylist: PlaylistSummary | null;
  isOverDropZone: boolean;

  // Operation selection
  showSelector: boolean;
  selectedOperation: SetOperationType | null;
  matchMode: MatchMode;

  // Preview (instant, STRICT mode)
  previewCount: number | null;

  // Result state
  resultTracks: Track[] | null;
  resultMetadata: SetOpsResultMetadata | null;
  isExecuting: boolean;
  error: string | null;

  // Actions
  startDrag: (playlist: PlaylistSummary) => void;
  endDrag: () => void;
  setOverDropZone: (isOver: boolean) => void;
  showOperationSelector: () => void;
  hideOperationSelector: () => void;
  selectOperation: (operation: SetOperationType) => void;
  setMatchMode: (mode: MatchMode) => void;

  // API actions
  fetchPreview: (trackIdsA: string[], trackIdsB: string[], operation: SetOperationType) => Promise<void>;
  executeOperation: (sourceAUri: string, sourceBUri: string) => Promise<Track[]>;

  // Result management
  clearResult: () => void;
  reset: () => void;
}

/**
 * SetOps store implementation
 */
export const useSetOpsStore = create<SetOpsState>((set, get) => ({
  // Initial state
  isDragging: false,
  draggedPlaylist: null,
  isOverDropZone: false,
  showSelector: false,
  selectedOperation: null,
  matchMode: 'smart',
  previewCount: null,
  resultTracks: null,
  resultMetadata: null,
  isExecuting: false,
  error: null,

  // Drag actions
  startDrag: (playlist) => set({
    isDragging: true,
    draggedPlaylist: playlist
  }),

  endDrag: () => set({
    isDragging: false,
    isOverDropZone: false,
  }),

  setOverDropZone: (isOver) => set({ isOverDropZone: isOver }),

  // Selector actions
  showOperationSelector: () => set({ showSelector: true }),

  hideOperationSelector: () => set({
    showSelector: false,
    selectedOperation: null,
    previewCount: null,
    // Also end the drag state when selector is closed
    isDragging: false,
    isOverDropZone: false,
  }),

  selectOperation: (operation) => set({ selectedOperation: operation }),

  setMatchMode: (mode) => set({ matchMode: mode }),

  // API actions
  fetchPreview: async (trackIdsA, trackIdsB, operation) => {
    try {
      const client = getApiClient();
      const response = await client.post<SetOpPreviewResponse>('/setops/preview', {
        operation,
        track_ids_a: trackIdsA,
        track_ids_b: trackIdsB,
      });
      set({ previewCount: response.estimated_count });
    } catch (error) {
      console.error('Preview failed:', error);
      set({ previewCount: null });
    }
  },

  executeOperation: async (sourceAUri, sourceBUri) => {
    const { selectedOperation, matchMode } = get();

    if (!selectedOperation) {
      throw new Error('No operation selected');
    }

    set({ isExecuting: true, error: null });

    try {
      const client = getApiClient();
      const response = await client.post<SetOpExecuteResponse>('/setops/execute', {
        operation: selectedOperation,
        source_a_uri: sourceAUri,
        source_b_uri: sourceBUri,
        match_mode: matchMode,
      });

      const result = response;

      set({
        resultTracks: result.result_tracks,
        resultMetadata: {
          operation: result.operation,
          sourceAName: result.source_a_name,
          sourceBName: result.source_b_name,
          matchMode: result.match_mode,
          duplicatesFound: result.duplicates_found,
          isrcMatches: result.isrc_matches,
          fuzzyMatches: result.fuzzy_matches,
          executionTimeMs: result.execution_time_ms,
        },
        isExecuting: false,
        showSelector: false,
      });

      return result.result_tracks;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Operation failed';
      set({
        error: errorMessage,
        isExecuting: false,
      });
      throw error;
    }
  },

  // Result management
  clearResult: () => set({
    resultTracks: null,
    resultMetadata: null,
    draggedPlaylist: null,
    error: null,
  }),

  reset: () => set({
    isDragging: false,
    draggedPlaylist: null,
    isOverDropZone: false,
    showSelector: false,
    selectedOperation: null,
    matchMode: 'smart',
    previewCount: null,
    resultTracks: null,
    resultMetadata: null,
    isExecuting: false,
    error: null,
  }),
}));
